import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon, XIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ExtractionPanelForm } from 'dashboard/components/ExtractionPanel/util';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { RegexExtractor, TextExtractor } from 'dashboard/models/document';
import Button, { IconButton } from 'design-system/components/Button';
import ListBox from 'design-system/components/ListBox/ListBox';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

const ListBoxContainer = styled.div`
  padding: ${Spacing[1]};
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const ListBoxItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: ${Spacing[1]} ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  color: ${({ theme, selected }) => (selected ? theme.color.variants.primary.default : 'inherit')};
  background-color: ${({ theme, selected }) =>
    selected ? theme.color.variants.primary.highlightLight : 'inherit'};
  transition-duration: ${TransitionDuration.Fast};
  user-select: none;

  &:hover {
    background-color: ${Color.CoreSlate50};
  }
`;

const MaximizablePanelStyles = () => css`
  width: 400px;
  top: 400px;
  min-height: unset;

  #maximizable-panel__toggle-fullscreen {
    display: none;
  }
`;

interface ExtractionPanelListBoxProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  name: 'textExtractors' | 'regexExtractors';
  title: string;
  extractors: Partial<TextExtractor>[] | Partial<RegexExtractor>[];
  disabled?: boolean;
  addExtractor: () => void;
  deleteExtractor: (index: number) => void;
  renderRow: (
    extractor: Partial<TextExtractor> | Partial<RegexExtractor>,
    index: number,
  ) => JSX.Element;
  panel: (index: number) => JSX.Element;
}

const ExtractionPanelListBox = ({
  formReturn,
  name,
  title,
  extractors,
  disabled = false,
  addExtractor,
  deleteExtractor,
  renderRow,
  panel,
}: ExtractionPanelListBoxProps) => {
  const { trigger } = formReturn;

  const [selectedExtractor, setSelectedExtractor] = useState<number | null>(null);

  const handleSelect = (index: number | null) => {
    setSelectedExtractor(index);
    if (index == null) {
      trigger(name);
    }
  };

  const handleClose = () => {
    handleSelect(null);
  };

  const handleAdd = () => {
    addExtractor();
    setSelectedExtractor(extractors.length);
  };

  const handleDelete = () => {
    if (selectedExtractor != null) {
      deleteExtractor(selectedExtractor);
      setSelectedExtractor(null);
    }
  };

  return (
    <div style={{ pointerEvents: disabled ? 'none' : 'auto' }}>
      <ListBoxContainer>
        <ListBox.Header>EXTRACTORS</ListBox.Header>
        {extractors?.map((extractor, index) => (
          <ListBoxItem
            key={`extraction-panel-list-box__list-box-item-${index}`}
            id={`extraction-panel-list-box__list-box-item-${index}`}
            selected={index === selectedExtractor}
            onClick={() => {
              handleSelect(selectedExtractor === index ? null : index);
            }}
          >
            {renderRow(extractor, index)}
          </ListBoxItem>
        ))}
        {(!extractors || extractors.length === 0) && (
          <ListBoxItem
            id='extraction-panel-list-box__list-box-empty'
            selected={false}
            onClick={handleAdd}
          >
            <Paragraph>None</Paragraph>
          </ListBoxItem>
        )}
      </ListBoxContainer>
      <Spacer height={1} />
      <HStack spacing={Spacing[1]}>
        <Button
          id='extraction-panel-list-box__add-button'
          variant='basic'
          width='auto'
          size='sm'
          onClick={handleAdd}
          disabled={disabled}
        >
          Add
        </Button>
      </HStack>
      {selectedExtractor != null && (
        <MaximizablePanel
          title={title}
          offsetElementId='extraction-panel__form'
          outsideExceptionalIdPrefixes={[
            `extraction-panel-list-box__list-box-item-${selectedExtractor}`,
          ]}
          onOutsideClickAndEscPress={handleClose}
          getContainerStyle={MaximizablePanelStyles}
          rightButton={
            <>
              <IconButton id='extraction-panel-list-box__delete-button' onClick={handleDelete}>
                <TrashIcon />
              </IconButton>
              <Spacer width={1} />
              <IconButton id='extraction-panel-list-box__close-button' onClick={handleClose}>
                <XIcon />
              </IconButton>
            </>
          }
        >
          {panel(selectedExtractor)}
        </MaximizablePanel>
      )}
    </div>
  );
};

export default ExtractionPanelListBox;
